import {
  Button,
  GroupTextField,
  GroupTextFieldRow,
  Icon,
  Input,
} from "@elaniintc/goes-design-system";
import { useMemo, useState } from "react";
import { useExpedienteNode } from "../context";
import type { LinkUserEntry, LinkExpedientEntry } from "../types";
import type { Variable } from "../../types";
import {
  DropdownList,
  type IDropdownListItem,
} from "@/components/ui/dropdown-list/dropdown-list";
import { nodeVariableToGroups } from "../../types";

const MAX_USERS = 10;
const MAX_EXPEDIENTS = 10;

// Combined action type: link/unlink + users/expedients
type CombinedActionType =
  | "link_users"
  | "unlink_users"
  | "link_expedients"
  | "unlink_expedients";

const combinedActionOptions: IDropdownListItem[] = [
  {
    value: "link_users",
    label: "Vincular usuarios",
    icon: { type: "far", name: "user-plus" },
  },
  {
    value: "unlink_users",
    label: "Desvincular usuarios",
    icon: { type: "far", name: "user-minus" },
  },
  {
    value: "link_expedients",
    label: "Vincular registros",
    icon: { type: "far", name: "folder-plus" },
  },
  {
    value: "unlink_expedients",
    label: "Desvincular registros",
    icon: { type: "far", name: "folder-minus" },
  },
];

export const ExpedienteLinkForm = () => {
  const {
    // Users state
    linkAction,
    setLinkAction,
    linkUsers,
    setLinkUsers,
    entityRoles,
    // Expedients state
    linkExpedientsAction,
    setLinkExpedientsAction,
    linkExpedients,
    setLinkExpedients,
    // Link sub-action
    linkSubAction,
    setLinkSubAction,
    // Variables
    allInputVariables: inputVariables,
  } = useExpedienteNode();

  // Search state for action selector
  const [actionSearch, setActionSearch] = useState("");

  // Derive the combined action from linkSubAction and linkAction/linkExpedientsAction
  const combinedAction: CombinedActionType = useMemo(() => {
    if (linkSubAction === "link_users") {
      return linkAction === "link" ? "link_users" : "unlink_users";
    } else {
      return linkExpedientsAction === "link"
        ? "link_expedients"
        : "unlink_expedients";
    }
  }, [linkSubAction, linkAction, linkExpedientsAction]);

  // Handle combined action change
  const handleCombinedActionChange = (action: CombinedActionType) => {
    if (action === "link_users" || action === "unlink_users") {
      setLinkSubAction("link_users");
      setLinkAction(action === "link_users" ? "link" : "unlink");
    } else {
      setLinkSubAction("link_expedients");
      setLinkExpedientsAction(action === "link_expedients" ? "link" : "unlink");
    }
  };

  const selectedActionOption = useMemo(() => {
    return combinedActionOptions.find((opt) => opt.value === combinedAction);
  }, [combinedAction]);

  // Role options for users
  const roleOptions: IDropdownListItem[] = useMemo(() => {
    return entityRoles.map((role: { id: string; name: string }) => ({
      id: role.id,
      value: role.id,
      label: role.name,
      icon: { type: "far" as const, name: "list" },
    }));
  }, [entityRoles]);

  // Variable groups
  const variableGroups = useMemo(
    () => nodeVariableToGroups(inputVariables),
    [inputVariables],
  );

  // User management functions
  const addUser = () => {
    if (linkUsers.length >= MAX_USERS) return;
    setLinkUsers([
      ...linkUsers,
      {
        id: crypto.randomUUID(),
        document: "",
        documentInputType: "inp",
        email: "",
        emailInputType: "inp",
        roleId: undefined,
        roleInputType: "inp",
      },
    ]);
  };

  const updateUser = (index: number, updates: Partial<LinkUserEntry>) => {
    const newUsers = [...linkUsers];
    newUsers[index] = { ...newUsers[index], ...updates };
    setLinkUsers(newUsers);
  };

  const removeUser = (index: number) => {
    if (linkUsers.length <= 1) return;
    setLinkUsers(linkUsers.filter((_, i) => i !== index));
  };

  // Expedient management functions
  const addExpedient = () => {
    if (linkExpedients.length >= MAX_EXPEDIENTS) return;
    setLinkExpedients([
      ...linkExpedients,
      {
        id: crypto.randomUUID(),
        expedientDocument: "",
        inputType: "inp",
      },
    ]);
  };

  const updateExpedient = (
    index: number,
    updates: Partial<LinkExpedientEntry>,
  ) => {
    const newExpedients = [...linkExpedients];
    newExpedients[index] = { ...newExpedients[index], ...updates };
    setLinkExpedients(newExpedients);
  };

  const removeExpedient = (index: number) => {
    if (linkExpedients.length <= 1) return;
    setLinkExpedients(linkExpedients.filter((_, i) => i !== index));
  };

  const isUsersMode =
    combinedAction === "link_users" || combinedAction === "unlink_users";
  const isLinkMode =
    combinedAction === "link_users" || combinedAction === "link_expedients";

  return (
    <div className="p-4 flex flex-col gap-4">
      {/* Section Title */}
      <div className="flex flex-col gap-1">
        <h3 className="text-body-loud font-semibold text-base">
          {isUsersMode ? "Usuarios a asociar" : "Registros a asociar"}
        </h3>
      </div>

      {/* Combined Action Selector */}
      <GroupTextField label="Acción de asociación" required>
        <DropdownList
          options={combinedActionOptions}
          selectedValue={selectedActionOption}
          toggleSelection={(item: IDropdownListItem) => {
            handleCombinedActionChange(item.value as CombinedActionType);
          }}
          placeholder="Selecciona una acción"
          search={{
            placeholder: "Buscar...",
            query: actionSearch,
            onSearch: setActionSearch,
          }}
          widthClassName="w-full"
          heightClassName="max-h-60"
        />
      </GroupTextField>

      {/* Users Form */}
      {isUsersMode && (
        <>
          <div className="flex flex-col gap-2">
            <div className="flex flex-col gap-3">
              {linkUsers.map((user: LinkUserEntry, index: number) => (
                <UserEntryCard
                  key={user.id}
                  user={user}
                  index={index}
                  onUpdate={(updates) => updateUser(index, updates)}
                  onRemove={() => removeUser(index)}
                  canRemove={linkUsers.length > 1}
                  variableGroups={variableGroups}
                  roleOptions={roleOptions}
                  inputVariables={inputVariables}
                  showRole={isLinkMode}
                />
              ))}

              {/* Add User Button */}
              {linkUsers.length < MAX_USERS && (
                <div className="flex items-center gap-3 mt-6 mb-4 justify-center">
                  <div className="flex-1 h-px border-neutral-quiet border" />
                  <Button
                    variant="ghost"
                    onClick={addUser}
                    type="button"
                    className="text-body-quiet font-semibold text-sm"
                  >
                    Añadir usuario <Icon type="fas" name="plus" />
                  </Button>
                  <div className="flex-1 border-neutral-quiet border" />
                </div>
              )}
            </div>
          </div>
        </>
      )}

      {/* Expedients Form */}
      {!isUsersMode && (
        <>
          <div className="flex flex-col gap-2">
            <label className="font-semibold text-body-loud leading-l">
              Registros a vincular
              <span className="text-danger">*</span>
            </label>
            <div className="flex flex-col gap-3">
              {linkExpedients.map(
                (expedient: LinkExpedientEntry, index: number) => (
                  <ExpedientEntryCard
                    key={expedient.id}
                    expedient={expedient}
                    onUpdate={(updates) => updateExpedient(index, updates)}
                    onRemove={() => removeExpedient(index)}
                    canRemove={linkExpedients.length > 1}
                    variableGroups={variableGroups}
                    inputVariables={inputVariables}
                  />
                ),
              )}

              {/* Add Expedient Button */}
              {linkExpedients.length < MAX_EXPEDIENTS && (
                <Button
                  variant="ghost"
                  onClick={addExpedient}
                  type="button"
                  className="text-body-quiet font-semibold text-sm w-fit"
                >
                  Añadir registro <Icon type="fas" name="plus" />
                </Button>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

// User Entry Card Component
interface UserEntryCardProps {
  user: LinkUserEntry;
  index: number;
  onUpdate: (updates: Partial<LinkUserEntry>) => void;
  onRemove: () => void;
  canRemove: boolean;
  variableGroups: ReturnType<typeof nodeVariableToGroups>;
  roleOptions: IDropdownListItem[];
  inputVariables: ReturnType<typeof useExpedienteNode>["allInputVariables"];
  showRole: boolean;
}

const UserEntryCard = ({
  user,
  index,
  onUpdate,
  onRemove,
  canRemove,
  variableGroups,
  roleOptions,
  inputVariables,
  showRole,
}: UserEntryCardProps) => {
  const [documentSearch, setDocumentSearch] = useState("");
  const [emailSearch, setEmailSearch] = useState("");
  const [roleSearch, setRoleSearch] = useState("");

  return (
    <div className="flex items-start gap-2">
      <div className="flex-1 flex flex-col gap-1">
        <div className="flex items-center justify-between">
          <label className="font-semibold text-body-loud leading-l">
            Usuario {index + 1}
            <span className="text-danger">*</span>
          </label>
        </div>

        <GroupTextField label="" className="flex-1">
          {/* Document Field */}
          <GroupTextFieldRow>
            <Button
              variant="quiet"
              type="button"
              className="font-mono"
              group="left"
              onClick={() =>
                onUpdate({
                  documentInputType:
                    user.documentInputType === "inp" ? "var" : "inp",
                  document: "",
                })
              }
            >
              {user.documentInputType === "inp" ? "inp" : "var"}
            </Button>
            {user.documentInputType === "inp" ? (
              <div className="relative flex-1 flex items-center -ml-px">
                <Icon
                  type="far"
                  name="id-card"
                  className="absolute left-3 text-body-quiet z-10"
                />
                <Input
                  group={showRole ? "up-right" : "right"}
                  className="w-full pl-10"
                  placeholder="ID del usuario"
                  value={typeof user.document === "string" ? user.document : ""}
                  onChange={(e) => onUpdate({ document: e.target.value })}
                />
              </div>
            ) : (
              <DropdownList
                options={variableGroups}
                selectedValue={
                  typeof user.document !== "string" && user.document
                    ? variableGroups
                        .flatMap((g) => g.items)
                        .find(
                          (item: IDropdownListItem) =>
                            item.value === (user.document as Variable).id,
                        )
                    : undefined
                }
                toggleSelection={(item: IDropdownListItem) => {
                  const variable = inputVariables
                    .flatMap((nv) => nv.variables)
                    .find((v) => v.id === item.value);
                  if (variable) {
                    onUpdate({ document: variable });
                  }
                }}
                placeholder="Selecciona una variable"
                search={{
                  placeholder: "Buscar variable...",
                  query: documentSearch,
                  onSearch: setDocumentSearch,
                }}
                group={showRole ? "up-right" : "right"}
                widthClassName="flex-1"
              />
            )}
          </GroupTextFieldRow>

          {/* Email Field */}
          <GroupTextFieldRow>
            <Button
              variant="quiet"
              type="button"
              className="font-mono border-t-0"
              group="left"
              onClick={() =>
                onUpdate({
                  emailInputType: user.emailInputType === "inp" ? "var" : "inp",
                  email: "",
                })
              }
            >
              {user.emailInputType === "inp" ? "inp" : "var"}
            </Button>
            {user.emailInputType === "inp" ? (
              <div className="relative flex-1 flex items-center -ml-px">
                <Icon
                  type="far"
                  name="envelope"
                  className="absolute left-3 text-body-quiet z-10"
                />
                <Input
                  group={showRole ? "middle-right" : "down-right"}
                  className="w-full pl-10 border-t-0"
                  placeholder="Correo del usuario"
                  value={typeof user.email === "string" ? user.email : ""}
                  onChange={(e) => onUpdate({ email: e.target.value })}
                />
              </div>
            ) : (
              <DropdownList
                options={variableGroups}
                selectedValue={
                  typeof user.email !== "string" && user.email
                    ? variableGroups
                        .flatMap((g) => g.items)
                        .find(
                          (item: IDropdownListItem) =>
                            item.value === (user.email as Variable).id,
                        )
                    : undefined
                }
                toggleSelection={(item: IDropdownListItem) => {
                  const variable = inputVariables
                    .flatMap((nv) => nv.variables)
                    .find((v) => v.id === item.value);
                  if (variable) {
                    onUpdate({ email: variable });
                  }
                }}
                placeholder="Selecciona una variable"
                search={{
                  placeholder: "Buscar variable...",
                  query: emailSearch,
                  onSearch: setEmailSearch,
                }}
                group={showRole ? "middle-right" : "down-right"}
                widthClassName="flex-1"
              />
            )}
          </GroupTextFieldRow>

          {/* Role Field (only for link action) */}
          {showRole && (
            <GroupTextFieldRow>
              <Button
                variant="quiet"
                type="button"
                className="font-mono border-t-0"
                group="left"
                onClick={() =>
                  onUpdate({
                    roleInputType: user.roleInputType === "inp" ? "var" : "inp",
                    roleId: undefined,
                  })
                }
              >
                {user.roleInputType === "inp" || !user.roleInputType
                  ? "inp"
                  : "var"}
              </Button>
              {user.roleInputType === "inp" || !user.roleInputType ? (
                <DropdownList
                  options={roleOptions}
                  selectedValue={
                    typeof user.roleId === "string"
                      ? roleOptions.find((opt) => opt.value === user.roleId)
                      : undefined
                  }
                  toggleSelection={(item: IDropdownListItem) => {
                    onUpdate({ roleId: item.value as string });
                  }}
                  placeholder="Rol del usuario"
                  search={{
                    placeholder: "Buscar rol...",
                    query: roleSearch,
                    onSearch: setRoleSearch,
                  }}
                  group="down-right"
                  widthClassName="flex-1"
                  heightClassName="max-h-50"
                />
              ) : (
                <DropdownList
                  options={variableGroups}
                  selectedValue={
                    typeof user.roleId !== "string" && user.roleId
                      ? variableGroups
                          .flatMap((g) => g.items)
                          .find(
                            (item: IDropdownListItem) =>
                              item.value === (user.roleId as Variable).id,
                          )
                      : undefined
                  }
                  toggleSelection={(item: IDropdownListItem) => {
                    const variable = inputVariables
                      .flatMap((nv) => nv.variables)
                      .find((v) => v.id === item.value);
                    if (variable) {
                      onUpdate({ roleId: variable });
                    }
                  }}
                  placeholder="Selecciona una variable"
                  search={{
                    placeholder: "Buscar variable...",
                    query: roleSearch,
                    onSearch: setRoleSearch,
                  }}
                  group="down-right"
                  widthClassName="flex-1"
                />
              )}
            </GroupTextFieldRow>
          )}
        </GroupTextField>
      </div>

      {/* Remove Button */}
      {canRemove && (
        <Button
          variant="ghost"
          size="icon"
          onClick={onRemove}
          className="mt-7"
          aria-label="Eliminar usuario"
        >
          <Icon name="times" type="far" />
        </Button>
      )}
    </div>
  );
};

// Expedient Entry Card Component
interface ExpedientEntryCardProps {
  expedient: LinkExpedientEntry;
  onUpdate: (updates: Partial<LinkExpedientEntry>) => void;
  onRemove: () => void;
  canRemove: boolean;
  variableGroups: ReturnType<typeof nodeVariableToGroups>;
  inputVariables: ReturnType<typeof useExpedienteNode>["allInputVariables"];
}

const ExpedientEntryCard = ({
  expedient,
  onUpdate,
  onRemove,
  canRemove,
  variableGroups,
  inputVariables,
}: ExpedientEntryCardProps) => {
  const [documentSearch, setDocumentSearch] = useState("");

  return (
    <div className="flex items-start gap-2">
      <div className="flex-1">
        <GroupTextField label="" className="flex-1">
          <GroupTextFieldRow>
            <Button
              variant="quiet"
              type="button"
              className="font-mono"
              group="left"
              onClick={() =>
                onUpdate({
                  inputType: expedient.inputType === "inp" ? "var" : "inp",
                  expedientDocument: "",
                })
              }
            >
              {expedient.inputType === "inp" ? "inp" : "var"}
            </Button>
            {expedient.inputType === "inp" ? (
              <div className="relative flex-1 flex items-center -ml-px">
                <Input
                  group="right"
                  className="w-full"
                  placeholder="ID del registro de expediente"
                  value={
                    typeof expedient.expedientDocument === "string"
                      ? expedient.expedientDocument
                      : ""
                  }
                  onChange={(e) =>
                    onUpdate({ expedientDocument: e.target.value })
                  }
                />
              </div>
            ) : (
              <DropdownList
                options={variableGroups}
                selectedValue={
                  typeof expedient.expedientDocument !== "string" &&
                  expedient.expedientDocument
                    ? variableGroups
                        .flatMap((g) => g.items)
                        .find(
                          (item: IDropdownListItem) =>
                            item.value ===
                            (expedient.expedientDocument as Variable).id,
                        )
                    : undefined
                }
                toggleSelection={(item: IDropdownListItem) => {
                  const variable = inputVariables
                    .flatMap((nv) => nv.variables)
                    .find((v) => v.id === item.value);
                  if (variable) {
                    onUpdate({ expedientDocument: variable });
                  }
                }}
                placeholder="Selecciona una variable"
                search={{
                  placeholder: "Buscar variable...",
                  query: documentSearch,
                  onSearch: setDocumentSearch,
                }}
                group="right"
                widthClassName="flex-1"
              />
            )}
          </GroupTextFieldRow>
        </GroupTextField>
      </div>

      {/* Remove Button */}
      {canRemove && (
        <Button
          variant="ghost"
          size="icon"
          onClick={onRemove}
          className="mt-1"
          aria-label="Eliminar registro"
        >
          <Icon name="times" type="far" />
        </Button>
      )}
    </div>
  );
};
